/**
 * T-1g-READ — the live READ client (`GET /mark/decisions`, `GET /mark/actions`).
 *
 * The write half of this app has always been correct; the read half did not
 * exist. Decisions and the Action List rendered from hardcoded constants, so a
 * resolve committed, delivered, and changed nothing on screen — permanently,
 * refresh or not. This is the client end of the seam that closes that
 * (goalkeeper `POST /read` ← coordinator `GET /mark/{decisions,actions}`).
 *
 * Two rules shape everything here:
 *
 *  1. **A failed read is never an empty list.** Returning `[]` on a network
 *     error would tell a superintendent "you have no open decisions" — the most
 *     dangerous possible lie for this product, and indistinguishable from the
 *     truth. Every result is a tagged union; the caller must handle `error`
 *     explicitly and falls back to last-known, never to nothing.
 *  2. **Offline is normal, not exceptional.** This is a no-WiFi field app
 *     (invariant 5). A read failure is an expected daily occurrence on a job
 *     site, so it degrades to the cached view rather than blocking the tab.
 */

import type { Decision } from "../slice/decision-slice";
import type { MasterItem } from "../components/MasterActionListView";
import type { StatusValue, ItemType } from "../components/MasterActionListView";
import type { TransportConfig } from "../outbox/http-transport";
import { deviceToken } from "../identity/device-token";
import { credentialFetch as fetch } from "../auth/credential-fetch";
import { parseReportDest } from "../triage/serialize-dest";

/** A read either produced a server view, or it did not — and says which. */
export type ReadOutcome<T> =
  | { ok: true; items: T[] }
  /** `retryable` false = the server refused us (authz/shape); true = the read
   * may succeed later (offline, 5xx). The UI wording differs: a refusal is
   * worth surfacing, a dropped signal is not alarming. */
  | { ok: false; error: string; retryable: boolean };

export type DiscardOutcome =
  | { ok: true }
  | { ok: false; error: string; retryable: boolean };

export interface ReadScope {
  project_ref: string;
}

export interface ReadClient {
  fetchDecisions(scope: ReadScope, includeResolved?: boolean): Promise<ReadOutcome<Decision>>;
  fetchActions(scope: ReadScope): Promise<ReadOutcome<MasterItem>>;
  fetchLogs(scope: ReadScope): Promise<ReadOutcome<LogEntry>>;
  discardLog(capture_id: string, scope: ReadScope): Promise<DiscardOutcome>;
  /** Who this device's bearer is, and what it may reach. */
  fetchWhoami(): Promise<Whoami | null>;
}

/** The caller's own identity and scope (`GET /mark/whoami`).
 *
 * The app previously built its project picker from a COMPILE-TIME fixture, which
 * is one shared list. Once people have their own codes a person scoped to one
 * project was still offered the others, and only found out by tapping one and
 * getting a 403 — offering someone a locked door is worse than not showing it. */
export interface Whoami {
  tenant_id: string;
  actor_id: string;
  role: string;
  project_ids: string[];
  /** False for a tenant_admin, whose project check is skipped entirely — so an
   * empty `project_ids` means EVERYTHING in the tenant, not nothing. Reading it
   * as "nothing" would render a picker with no options at all. */
  scoped: boolean;
}

/** One collapsed daily-log entry as the server folds it (`GET /mark/logs`).
 *
 * The server view of an entry, NOT an outbox record: corrections are already
 * folded in, removals are already withheld, and `id` is the canonical's capture
 * id even when a correction supplied the text. Entries this device captured
 * itself are rendered from its own outbox (which also holds the photo blobs);
 * this type backfills the ones it has no record of — after a factory reset, on a
 * second device, or for a principal reading a project they did not capture. */
export interface LogEntry {
  id: string;
  kind: string;
  text: string;
  ts: string;
  state: string;
  source_refs: string[];
  /** A correction won — the entry reads differently than as first captured. */
  amended: boolean;
  correction_note?: string;
  /** Highest ordinal already spent on this entry. A correction must allocate
   * ABOVE it, or the fold keeps the older one and the edit silently loses. */
  change_ordinal: number;
  criticality_tier?: string;
  owner?: string;
  room?: string;
  decides?: string;
  /** Which reports this entry is currently on — the same shape `serializeDest`
   * writes, so `deserializeDest` reads it back with the same reader.
   *
   * Null/absent means never pinned, which the projection treats as legacy (both
   * windows). An explicit `[]` means the human took it off every report, and the
   * two must stay distinguishable: without this field the edit screen rendered
   * its destination boxes blank on an entry that WAS on the daily report, so
   * there was nothing to un-tick (Red, 2026-07-31). */
  report_dest?: "internal_only" | Array<"daily" | "weekly"> | null;
  /** Who FILED the entry. The id is the stable identity; `author_name` is how to
   * show them, and is absent when nobody has set a name — render the id then
   * rather than nothing. */
  author?: string;
  author_name?: string;
  /** Who last CORRECTED it, and when. Both absent on an unamended entry AND when
   * someone corrected their own entry — the server suppresses "edited by A" on
   * A's own entry so the client never has to know that rule. */
  edited_by?: string;
  edited_by_name?: string;
  edited_ts?: string;
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/** Wire → `Decision`. Mapped field by field rather than cast, so a wire change
 * surfaces as a missing field here instead of as `undefined` deep in a view. */
export function decisionFromWire(raw: Record<string, unknown>): Decision {
  return {
    id: String(raw.id ?? ""),
    decision_id: String(raw.decision_id ?? ""),
    text: String(raw.text ?? ""),
    subject: str(raw.subject),
    phase: str(raw.phase),
    room_zone: str(raw.room_zone),
    trade: str(raw.trade),
    system: str(raw.system),
    item_type: str(raw.item_type),
    decides: str(raw.decides),
    input: str(raw.input),
    owner: str(raw.owner),
    assignee: str(raw.assignee),
    owner_needed: typeof raw.owner_needed === "boolean" ? raw.owner_needed : undefined,
    criticality_tier: str(raw.criticality_tier),
    status: raw.status === "resolved" ? "resolved" : "open",
    first_raised_ts: String(raw.first_raised_ts ?? ""),
    resolved_ts: str(raw.resolved_ts),
    rationale: str(raw.rationale),
    source_refs: Array.isArray(raw.source_refs) ? raw.source_refs.map(String) : [],
    template_version: str(raw.template_version),
  };
}

/** Wire → `MasterItem`. The wire also carries `gates`/`lead_time`/`phase`/`zone`
 * JSON envelopes; they are deliberately NOT spread in, because the UI type's
 * fields are plain strings and a spread would put objects where strings belong. */
export function masterItemFromWire(raw: Record<string, unknown>): MasterItem {
  return {
    id: String(raw.id ?? ""),
    text: String(raw.text ?? ""),
    room: str(raw.room),
    decides: str(raw.decides),
    description: str(raw.description),
    source_refs: Array.isArray(raw.source_refs) ? raw.source_refs.map(String) : [],
    status: (str(raw.status) ?? "can_do") as StatusValue,
    blocker: str(raw.blocker),
    criticality_tier: str(raw.criticality_tier),
    item_type: str(raw.item_type) as ItemType | undefined,
    owner: str(raw.owner),
    assignee: str(raw.assignee),
    trade: str(raw.trade),
    sub: str(raw.sub),
    first_raised_ts: String(raw.first_raised_ts ?? ""),
  };
}

/** Wire → `LogEntry`, mapped field by field for the same reason as the others. */
export function logEntryFromWire(raw: Record<string, unknown>): LogEntry {
  return {
    id: String(raw.id ?? ""),
    kind: String(raw.kind ?? ""),
    text: String(raw.text ?? ""),
    ts: String(raw.ts ?? ""),
    state: String(raw.state ?? ""),
    source_refs: Array.isArray(raw.source_refs)
      ? (raw.source_refs as unknown[]).filter((v): v is string => typeof v === "string")
      : [],
    amended: raw.amended === true,
    correction_note: str(raw.correction_note),
    change_ordinal: typeof raw.change_ordinal === "number" ? raw.change_ordinal : 0,
    criticality_tier: str(raw.criticality_tier),
    owner: str(raw.owner),
    room: str(raw.room),
    decides: str(raw.decides),
    author: str(raw.author),
    author_name: str(raw.author_name),
    edited_by: str(raw.edited_by),
    edited_by_name: str(raw.edited_by_name),
    edited_ts: str(raw.edited_ts),
    // Not `str()`: this value is a LIST or the literal "internal_only", and it
    // is the one field here where absent and empty mean different things —
    // absent is "never pinned" (both windows, the projection's legacy case) and
    // `[]` is "the human took it off every report". Collapsing them would make
    // removal unrepresentable, so the shape is preserved and anything
    // unrecognised is dropped to undefined rather than coerced.
    report_dest: destFromWire(raw.report_dest),
  };
}

/** The `report_dest` axis off the wire, or `undefined` when it is absent, null,
 * or a shape this build does not know — `deserializeDest` reads all three as
 * "never pinned", which is the safe reading: a save that does not touch the
 * destination boxes then changes nothing. */
function destFromWire(raw: unknown): LogEntry["report_dest"] {
  return parseReportDest(raw);
}

function itemsFrom(body: unknown): Record<string, unknown>[] {
  // The coordinator wraps the goalkeeper envelope in its ReplyEnvelope:
  // {status, payload: {skill_id, project_ref, count, items}}.
  const payload = (body as { payload?: { items?: unknown } } | null)?.payload;
  const items = payload?.items;
  return Array.isArray(items) ? (items as Record<string, unknown>[]) : [];
}

export class HttpReadClient implements ReadClient {
  constructor(private cfg: TransportConfig) {}

  private async get<T>(
    path: string,
    map: (raw: Record<string, unknown>) => T,
  ): Promise<ReadOutcome<T>> {
    let res: Response;
    try {
      res = await fetch(`${this.cfg.endpoint}${path}`, {
        headers: { Authorization: `Bearer ${this.cfg.token}` },
        credentials: "include",
      });
    } catch {
      // Offline / DNS / radio down — the ordinary field case.
      return { ok: false, error: "offline", retryable: true };
    }
    if (!res.ok) {
      let code = `http_${res.status}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (typeof body.error === "string") code = body.error;
      } catch {
        /* keep the status-derived code */
      }
      // 4xx is the server refusing this request as-shaped; 5xx is worth retrying.
      return { ok: false, error: code, retryable: res.status >= 500 };
    }
    try {
      const body = await res.json();
      return { ok: true, items: itemsFrom(body).map(map) };
    } catch {
      // A 200 we cannot parse is NOT an empty project — say so.
      return { ok: false, error: "unreadable_response", retryable: true };
    }
  }

  fetchDecisions(scope: ReadScope, includeResolved = false): Promise<ReadOutcome<Decision>> {
    const q = `?project_ref=${encodeURIComponent(scope.project_ref)}${
      includeResolved ? "&include_resolved=true" : ""
    }`;
    return this.get(`/mark/decisions${q}`, decisionFromWire);
  }

  /** Null on any failure — offline, an old coordinator with no such route, or an
   * unparseable body. The caller falls back to its baked project list, which is
   * the pre-existing behaviour: never leave someone unable to pick a project
   * because an identity lookup did not answer. */
  async fetchWhoami(): Promise<Whoami | null> {
    try {
      const res = await fetch(`${this.cfg.endpoint}/mark/whoami`, {
        headers: { Authorization: `Bearer ${this.cfg.token}` },
        credentials: "include",
      });
      if (!res.ok) return null;
      const body = (await res.json()) as { payload?: Record<string, unknown> };
      const p = body.payload;
      if (!p || typeof p.actor_id !== "string") return null;
      return {
        tenant_id: String(p.tenant_id ?? ""),
        actor_id: p.actor_id,
        role: String(p.role ?? ""),
        project_ids: Array.isArray(p.project_ids) ? p.project_ids.map(String) : [],
        scoped: p.scoped === true,
      };
    } catch {
      return null;
    }
  }

  fetchActions(scope: ReadScope): Promise<ReadOutcome<MasterItem>> {
    return this.get(
      `/mark/actions?project_ref=${encodeURIComponent(scope.project_ref)}`,
      masterItemFromWire,
    );
  }

  fetchLogs(scope: ReadScope): Promise<ReadOutcome<LogEntry>> {
    return this.get(
      `/mark/logs?project_ref=${encodeURIComponent(scope.project_ref)}`,
      logEntryFromWire,
    );
  }

  async discardLog(capture_id: string, scope: ReadScope): Promise<DiscardOutcome> {
    let res: Response;
    try {
      res = await fetch(
        `${this.cfg.endpoint}/mark/logs/${encodeURIComponent(capture_id)}?project_ref=${encodeURIComponent(scope.project_ref)}`,
        {
          method: "DELETE",
          body: undefined,
          headers: { Authorization: `Bearer ${this.cfg.token}` },
          credentials: "include",
        },
      );
    } catch {
      return { ok: false, error: "offline", retryable: true };
    }
    if (res.ok) return { ok: true };
    let error = `http_${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (typeof body.error === "string") error = body.error;
    } catch {
      // Keep the status-derived code.
    }
    return { ok: false, error, retryable: res.status >= 500 };
  }
}

/**
 * Build a read client from the baked build env, or `null` when no endpoint was
 * baked in. Unlike the send path — which throws at boot, because a PWA that
 * cannot send is a build error — a missing endpoint here is survivable: the
 * tabs fall back to their last-known cache. Throwing would take down surfaces
 * that have perfectly good cached content to show.
 */
export function makeReadClient(
  env: Record<string, string | undefined> = import.meta.env as unknown as Record<
    string,
    string | undefined
  >,
): ReadClient | null {
  const endpoint = env.VITE_MARK_ENDPOINT;
  if (!endpoint) return null;
  return new HttpReadClient({
    endpoint: endpoint.replace(/\/+$/, ""),
    // The READ path matters as much as the write path here: with the baked
    // bearer a per-person device would write as themselves but READ with the
    // shared identity's full project scope, which defeats the isolation
    // entirely — someone scoped to one project would still see the others.
    token: env.VITE_MARK_AUTH_STUB === "1"
      ? deviceToken()?.trim() || env.VITE_MARK_BEARER || ""
      : "",
  });
}
